import { useEffect, useRef } from 'react'

import { supportedLanguages } from '@common'
import cx from 'classnames'
import { LazyMotion, AnimatePresence, m } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'

import { useGameImages } from '@/components/games/images'
import { ExitIcon } from '@/components/Icons/ExitIcon'
import { useLocale } from '@/features/locale/useLocale'
import { useGameSlug } from '@/features/storefronts/game-provider'
import useOnClickOutside from '@/utils/useOnClickOutside'

import styles from './menuContainer.module.scss'

type Props = React.PropsWithChildren<{
  isOpen: boolean
  closeMenu: () => void
}>

const MenuContainer = ({ isOpen, closeMenu, children }: Props) => {
  const ref = useRef(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  useOnClickOutside(ref, closeMenu)
  const locale = useLocale()
  const features = () => import('@/animations/features').then((a) => a.default)
  const isRTL = supportedLanguages[locale].rightToLeft

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove('overflow-hidden')
    } else {
      document.body.classList.add('overflow-hidden')
      closeButtonRef.current?.focus()
    }

    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isOpen])

  const gameSlug = useGameSlug()
  const { gameImage } = useGameImages(gameSlug)

  return (
    <LazyMotion features={features}>
      <AnimatePresence>
        {isOpen && (
          <>
            <m.div
              key="menu-container"
              className={styles.container}
              initial={{ x: isRTL ? -500 : 500 }}
              animate={{ x: 0 }}
              exit={{ x: isRTL ? -500 : 500 }}
              transition={{ duration: 0.2 }}
            >
              <div className={cx(styles.header, styles[gameSlug])}>
                <Link href={`/${gameSlug}`} tabIndex={0} className={styles.logo}>
                  <Image src={gameImage('badge')} alt={gameSlug} height={54} quality={100} />
                </Link>
                <button className={styles.menuButton} onClick={closeMenu} ref={closeButtonRef}>
                  <ExitIcon />
                </button>
              </div>

              {children}
            </m.div>
            <m.div
              key="backdrop"
              className={styles.backdrop}
              onClick={closeMenu}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            ></m.div>
          </>
        )}
      </AnimatePresence>
    </LazyMotion>
  )
}

export default MenuContainer
